import SeoFooter from "../components/SeoFooter";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Phone, Mail, ChevronDown, HelpCircle, CreditCard, Plane, Car, Shield, Clock, Baby, ArrowRight
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const FAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqCategories = [
    {
      title: "Booking & Payments",
      icon: CreditCard,
      faqs: [
        {
          q: "How far in advance should I book my limousine?",
          a: "We recommend booking at least 24–48 hours in advance to guarantee your preferred vehicle. However, we do our best to accommodate last-minute requests based on availability."
        },
        {
          q: "Are there any hidden fees or surge pricing?",
          a: "No. At LimoNearMe, we believe in transparent pricing. Your quote includes GST, tolls, and parking fees. The price you see at the time of booking is the final price."
        },
        {
          q: "What is your cancellation policy?",
          a: "We offer free cancellations up to 24 hours before your scheduled pickup. Cancellations made within 24 hours may incur a fee."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for corporate accounts. Payment is processed securely through our encrypted payment gateway."
        },
        {
          q: "Can I get an invoice for my booking?",
          a: "Yes, we provide detailed invoices for all bookings. Corporate account holders receive consolidated monthly invoices. Individual customers receive an invoice via email after the trip is completed."
        }
      ]
    },
    {
      title: "Airport Transfers",
      icon: Plane,
      faqs: [
        {
          q: "What happens if my flight is delayed?",
          a: "Don't worry! We monitor all flight arrivals in real-time. Your chauffeur will adjust the pickup time accordingly at no extra cost to you."
        },
        {
          q: "Where do I meet my driver at the airport?",
          a: "For most arrivals, your driver will meet you at the 'Arrivals Hall' holding a digital sign with your name on it. We also send a text message with the driver's details and vehicle plate number 15 minutes before pickup."
        },
        {
          q: "How long will my driver wait for me at the airport?",
          a: "We include 60 minutes of complimentary waiting time for international arrivals and 30 minutes for domestic flights. Additional waiting time can be arranged if needed."
        },
        {
          q: "Do you service all Australian airports?",
          a: "We provide airport transfer services at all major Australian airports including Brisbane (BNE), Gold Coast (OOL), Sydney (SYD), Melbourne (MEL), Cairns (CNS), and Perth (PER)."
        }
      ]
    },
    {
      title: "Vehicles & Service",
      icon: Car,
      faqs: [
        {
          q: "Can you provide child car seats?",
          a: "Yes, we prioritize family safety. We can provide baby capsules, forward-facing seats, or boosters upon request. Please mention this in the 'Special Requests' section of your booking."
        },
        {
          q: "Are your chauffeurs insured and licensed?",
          a: "Absolutely. All our drivers undergo rigorous background checks and hold current commercial passenger vehicle licenses and full insurance."
        },
        {
          q: "Can I request a specific vehicle for my event?",
          a: "Yes. You can choose from our range of executive sedans, premium SUVs, Mercedes V-Class people movers, or Mercedes Sprinters during the booking process to suit your style and group size."
        },
        {
          q: "What amenities are available in your vehicles?",
          a: "Our vehicles feature leather interiors, climate control, complimentary bottled water, phone chargers, and Wi-Fi. Champagne service is available upon request for special occasions."
        },
        {
          q: "How many passengers can your vehicles accommodate?",
          a: "Our fleet ranges from executive sedans (up to 3 passengers) to Mercedes Sprinters (up to 15 passengers). We can accommodate groups of any size."
        }
      ]
    },
    {
      title: "Corporate & Events",
      icon: Shield,
      faqs: [
        {
          q: "Do you offer corporate accounts?",
          a: "Yes, we offer corporate accounts with benefits including priority booking, dedicated account management, flexible billing options, and detailed travel reports."
        },
        {
          q: "Can you handle transportation for large events?",
          a: "Absolutely. We specialize in event transportation for conferences, weddings, roadshows, and corporate gatherings. Our team can coordinate multiple vehicles to move your entire group seamlessly."
        },
        {
          q: "Is there a minimum booking requirement for corporate accounts?",
          a: "No minimum booking requirement. Corporate accounts are available to businesses of all sizes, from startups to large enterprises."
        },
        {
          q: "Do your chauffeurs sign NDAs?",
          a: "Yes, confidentiality is paramount. All our chauffeurs sign non-disclosure agreements to ensure your business conversations and personal information remain private."
        }
      ]
    }
  ];

  // Flatten FAQs for Schema
  const allFaqs = faqCategories.flatMap(cat => cat.faqs);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const toggleFaq = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFaq(openFaq === key ? null : key);
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>FAQ | Frequently Asked Questions | Limo Near Me Australia</title>
          <meta name="description" content="Find answers to common questions about LimoNearMe services. Booking, payments, airport transfers, vehicle options, child seats, corporate accounts, and cancellation policies." />
          <meta name="keywords" content="limo FAQ, limousine booking questions, airport transfer FAQ, chauffeur service questions, limo hire Australia FAQ, corporate transport FAQ" />
          <link rel="canonical" href="https://www.limonearme.com.au/faq" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <HelpCircle className="h-16 w-16 mx-auto mb-6 text-white/80" />
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Find answers to common questions about our limousine and chauffeur services across Australia.
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="mb-12"
              >
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <category.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">{category.title}</h2>
                </div>

                {/* FAQs */}
                <div className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => {
                    const key = `${categoryIndex}-${faqIndex}`;
                    const isOpen = openFaq === key;
                    
                    return (
                      <div 
                        key={faqIndex}
                        className="border border-black/10 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFaq(categoryIndex, faqIndex)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                        >
                          <h3 className="font-semibold text-foreground pr-4">{faq.q}</h3>
                          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5">
                            <p className="text-muted-foreground border-t border-black/10 pt-4">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-muted-foreground mb-8">
              Our team is available 24/7 to help with any questions or special requests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+61481437772">
                <Button size="lg" className="rounded-full px-8">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-8">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8">Quick Links</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { title: "Book a Transfer", link: "/book", icon: Car },
                { title: "Our Fleet", link: "/fleet", icon: Car },
                { title: "Airport Transfers", link: "/services/airport-transfers", icon: Plane },
                { title: "Corporate Services", link: "/corporate-travel", icon: Shield }
              ].map((item, i) => (
                <Link key={i} to={item.link}>
                  <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-md transition-shadow">
                    <item.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
                    <h3 className="font-semibold">{item.title}</h3>
                    <ArrowRight className="h-4 w-4 mx-auto mt-2 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default FAQPage;
